import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle, XCircle, Loader2, ExternalLink,
  ChevronDown, ChevronUp, Info, Filter, ChevronLeft, ChevronRight,
  Square, CheckSquare, Globe, BarChart3
} from 'lucide-react';

const EVENT_TYPE_MAP = {
  funding: { emoji: '\uD83D\uDCB0', label: 'Funding / Grants' },
  new_line: { emoji: '\uD83C\uDFED', label: 'New Production Line' },
  expansion: { emoji: '\uD83D\uDCC8', label: 'Expansion' },
  new_plant: { emoji: '\uD83C\uDFD7\uFE0F', label: 'New Plant / Facility' },
  acquisition: { emoji: '\uD83E\uDD1D', label: 'Acquisition' },
  leadership_change: { emoji: '\uD83D\uDC64', label: 'Leadership Change' },
};

const COUNTRY_NAMES = {
  FI: 'Finland', SE: 'Sweden', NO: 'Norway', DK: 'Denmark',
  EE: 'Estonia', LV: 'Latvia', LT: 'Lithuania',
  PL: 'Poland', CZ: 'Czech Republic', SK: 'Slovakia', HU: 'Hungary',
  RO: 'Romania', BG: 'Bulgaria',
  DE: 'Germany', AT: 'Austria', CH: 'Switzerland',
  NL: 'Netherlands', BE: 'Belgium',
  FR: 'France', ES: 'Spain', PT: 'Portugal', IT: 'Italy',
  GR: 'Greece', SI: 'Slovenia', HR: 'Croatia',
  IE: 'Ireland', GB: 'United Kingdom', US: 'United States',
  CA: 'Canada', AU: 'Australia', JP: 'Japan', CN: 'China',
  IN: 'India', BR: 'Brazil', MX: 'Mexico', KR: 'South Korea',
  RU: 'Russia', UA: 'Ukraine', TR: 'Turkey', IL: 'Israel',
  ZA: 'South Africa', SG: 'Singapore', MY: 'Malaysia', TH: 'Thailand',
  LU: 'Luxembourg', MT: 'Malta', CY: 'Cyprus', IS: 'Iceland',
  RS: 'Serbia', BA: 'Bosnia and Herzegovina', ME: 'Montenegro',
  MK: 'North Macedonia', AL: 'Albania', XK: 'Kosovo',
  AE: 'United Arab Emirates', SA: 'Saudi Arabia', KZ: 'Kazakhstan',
  GY: 'Guyana', CL: 'Chile', AR: 'Argentina', CO: 'Colombia',
  NZ: 'New Zealand', PH: 'Philippines', ID: 'Indonesia', VN: 'Vietnam',
  EU: 'European Union', NG: 'Nigeria', EG: 'Egypt', KE: 'Kenya',
};

const SOURCE_DISPLAY = {
  gunb_permits: { label: 'GUNB', color: 'bg-orange-100 text-orange-700' },
  gunb: { label: 'GUNB', color: 'bg-orange-100 text-orange-700' },
  sudop: { label: 'SUDOP', color: 'bg-purple-100 text-purple-700' },
  ted_tenders: { label: 'TED', color: 'bg-blue-100 text-blue-700' },
  ted_search: { label: 'TED', color: 'bg-blue-100 text-blue-700' },
  biznes_gov_pl: { label: 'Biznes.gov', color: 'bg-green-100 text-green-700' },
  przetargi_info: { label: 'Przetargi', color: 'bg-indigo-100 text-indigo-700' },
  sam_gov: { label: 'SAM.gov', color: 'bg-red-100 text-red-700' },
  hilma_fi: { label: 'Hilma', color: 'bg-sky-100 text-sky-700' },
  stip_fi: { label: 'STIP', color: 'bg-emerald-100 text-emerald-700' },
  google_news: { label: 'Google News', color: 'bg-gray-100 text-gray-700' },
  ezamowienia_bzp: { label: 'BZP', color: 'bg-amber-100 text-amber-700' },
};

const getSourceTags = (signal) => {
  const sources = new Set();
  if (signal.scored_signal_sources) {
    signal.scored_signal_sources.forEach(sss => {
      const src = sss.raw_signals?.source;
      if (src) sources.add(src);
    });
  }
  return [...sources];
};

const SignalReviewQueue = ({
  signals,
  loading,
  onApprove,
  onReject,
  onBatchApprove,
  onBatchReject,
  profileStats,
  page = 1,
  totalPages = 1,
  total = 0,
  pageSize = 10,
  onPageChange,
  filterOptions = { countries: [], eventTypes: [] },
  countryFilter = '',
  eventTypeFilter = '',
  minScoreFilter = 0,
  originFilter = '',
  onCountryFilterChange,
  onEventTypeFilterChange,
  onMinScoreFilterChange,
  onOriginFilterChange,
  onClearFilters,
  fetchAllMatchingIds,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  // Signals are already filtered server-side, use directly
  const displaySignals = signals || [];
  const hasActiveFilters = countryFilter || eventTypeFilter || minScoreFilter > 0 || originFilter;

  // Selection helpers
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = async () => {
    if (selectedIds.size > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectAllLoading(true);
      try {
        const allIds = await fetchAllMatchingIds();
        setSelectedIds(new Set(allIds));
      } catch (err) {
        console.error('Select all failed:', err);
      } finally {
        setSelectAllLoading(false);
      }
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Actions
  const handleAction = async (signalId, action) => {
    setActionLoading(signalId);
    try {
      if (action === 'approve') await onApprove(signalId);
      else await onReject(signalId);
      selectedIds.delete(signalId);
      setSelectedIds(new Set(selectedIds));
    } catch (err) {
      console.error(`Review ${action} failed:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBatchAction = async (action) => {
    if (selectedIds.size === 0) return;
    setBatchLoading(true);
    try {
      const ids = [...selectedIds];
      if (action === 'approve') await onBatchApprove(ids);
      else await onBatchReject(ids);
      setSelectedIds(new Set());
    } catch (err) {
      console.error(`Batch ${action} failed:`, err);
    } finally {
      setBatchLoading(false);
    }
  };

  // Parse contact_info (stored as JSON string or plain text)
  const parseContactInfo = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return { raw }; }
  };

  const getConfidenceColor = (conf) => {
    if (conf >= 0.8) return 'text-green-700 bg-green-50 border-green-200';
    if (conf >= 0.6) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-orange-700 bg-orange-50 border-orange-200';
  };

  // Pagination: generate page numbers (max 5 visible)
  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Loading review queue...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Stats banner */}
      {profileStats && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Info className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm text-blue-800">
                <strong>{profileStats.org_name}</strong>:&nbsp;
                {profileStats.approved_count || 0} approved,&nbsp;
                {profileStats.rejected_count || 0} rejected
                {profileStats.auto_approve && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    Auto-approve ON
                  </span>
                )}
              </span>
            </div>
            {!profileStats.auto_approve && (
              <span className="text-xs text-blue-600 font-medium">
                {Math.max(0, 20 - (profileStats.approved_count || 0) - (profileStats.rejected_count || 0))} reviews until auto-approve check
              </span>
            )}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-primary-50 text-primary-700 border border-primary-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[countryFilter, eventTypeFilter, minScoreFilter > 0].filter(Boolean).length}
              </span>
            )}
          </button>
          <span className="text-sm text-gray-500">
            {total} signal{total !== 1 ? 's' : ''} pending
            {hasActiveFilters && ' (filtered)'}
          </span>
        </div>

        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-xl"
          >
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Globe className="w-3 h-3 inline mr-1" />Country
                </label>
                <select
                  value={countryFilter}
                  onChange={(e) => onCountryFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All countries</option>
                  {filterOptions.countries.map(c => (
                    <option key={c.code} value={c.code}>
                      {COUNTRY_NAMES[c.code] || c.code} ({c.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Event Type</label>
                <select
                  value={eventTypeFilter}
                  onChange={(e) => onEventTypeFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All types</option>
                  {filterOptions.eventTypes.map(t => (
                    <option key={t.type} value={t.type}>
                      {EVENT_TYPE_MAP[t.type]?.emoji || ''} {EVENT_TYPE_MAP[t.type]?.label || t.type} ({t.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Origin</label>
                <select
                  value={originFilter}
                  onChange={(e) => onOriginFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All origins</option>
                  <option value="country_pack">Country Pack</option>
                  <option value="google_news">Google News / RSS</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <BarChart3 className="w-3 h-3 inline mr-1" />Min Score
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={minScoreFilter || ''}
                  onChange={(e) => onMinScoreFilterChange(parseInt(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="mt-3 text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear all filters
              </button>
            )}
          </motion.div>
        )}
      </div>

      {/* Bulk action bar + select all */}
      {displaySignals.length > 0 && (
        <div className="flex items-center justify-between mb-3 p-3 bg-white border border-gray-200 rounded-xl">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSelectAll}
              disabled={selectAllLoading}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-700 transition-colors disabled:opacity-50"
            >
              {selectAllLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : someSelected ? (
                <CheckSquare className="w-4 h-4 text-primary-600" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {selectAllLoading ? 'Selecting...' : someSelected ? 'Deselect all' : `Select all (${total})`}
            </button>
            {someSelected && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {selectedIds.size} of {total} selected
              </span>
            )}
          </div>

          {someSelected && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBatchAction('approve')}
                disabled={batchLoading}
                className="px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Approve ({selectedIds.size})
              </button>
              <button
                onClick={() => handleBatchAction('reject')}
                disabled={batchLoading}
                className="px-3 py-1.5 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Reject ({selectedIds.size})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Signal list */}
      {displaySignals.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {hasActiveFilters ? 'No signals match filters' : 'Review Queue Empty'}
          </h3>
          <p className="text-gray-600 text-sm">
            {hasActiveFilters
              ? 'Try adjusting your filter criteria.'
              : (profileStats?.auto_approve
                ? 'Auto-approve is active \u2014 new signals are approved automatically.'
                : 'New signals will appear here when the worker processes feeds.')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displaySignals.map((signal) => {
            const isExpanded = expandedId === signal.id;
            const isLoading = actionLoading === signal.id;
            const isSelected = selectedIds.has(signal.id);
            const contact = parseContactInfo(signal.contact_info);

            return (
              <motion.div
                key={signal.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white border rounded-xl overflow-hidden transition-colors ${
                  isSelected ? 'border-primary-300 ring-1 ring-primary-200' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Main row */}
                <div className="flex items-start p-4">
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleSelect(signal.id)}
                    className="mt-1 mr-3 shrink-0"
                  >
                    {isSelected ? (
                      <CheckSquare className="w-5 h-5 text-primary-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-300 hover:text-gray-500" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    {/* Header: company, country, event types, source tags, score */}
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h4 className="text-base font-semibold text-gray-900">{signal.company}</h4>
                      {signal.country && (
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium">{COUNTRY_NAMES[signal.country] || signal.country}</span>
                      )}
                      {signal.origin === 'country_pack' && (
                        <span className="text-[10px] bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded font-medium">CP</span>
                      )}
                      {signal.origin === 'google_news' && (
                        <span className="text-[10px] bg-gray-50 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded font-medium">RSS</span>
                      )}
                      {(signal.event_types || []).map(t => (
                        <span key={t} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                          {EVENT_TYPE_MAP[t]?.emoji || ''} {EVENT_TYPE_MAP[t]?.label || t}
                        </span>
                      ))}
                      {getSourceTags(signal).map(src => {
                        const display = SOURCE_DISPLAY[src] || { label: src.replace(/_/g, ' '), color: 'bg-gray-100 text-gray-600' };
                        return (
                          <span key={src} className={`text-xs px-2 py-0.5 rounded-full font-medium ${display.color}`}>
                            {display.label}
                          </span>
                        );
                      })}
                      {signal.source_count > 1 && (
                        <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">{signal.source_count} sources</span>
                      )}
                      {signal.purchase_window && (
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{signal.purchase_window}</span>
                      )}
                    </div>

                    {/* Summary */}
                    {signal.summary_en && (
                      <p className="text-sm text-gray-700 mt-1.5">{signal.summary_en}</p>
                    )}

                    {/* Quick stats row */}
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      {signal.why_now_en && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium text-gray-600">Why Now:</span> {signal.why_now_en}
                        </div>
                      )}
                    </div>

                    {/* Metadata badges */}
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {signal.purchase_window && (
                        <div className="text-xs">
                          <span className="text-gray-500">Window:</span>{' '}
                          <span className="font-medium text-gray-700">{signal.purchase_window}</span>
                        </div>
                      )}
                      {signal.size_class && (
                        <div className="text-xs">
                          <span className="text-gray-500">Deal Size:</span>{' '}
                          <span className="font-medium text-gray-700 capitalize">{signal.size_class}</span>
                        </div>
                      )}
                      <div className="text-xs">
                        <span className="text-gray-500">Confidence:</span>{' '}
                        <span className={`font-medium px-1.5 py-0.5 rounded ${getConfidenceColor(signal.confidence)}`}>
                          {(signal.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-500">Score:</span>{' '}
                        <span className="font-semibold text-gray-800">{signal.relevance_score}/100</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2 ml-4 shrink-0">
                    <div className="flex items-center gap-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleAction(signal.id, 'approve')}
                        disabled={isLoading}
                        className="px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        Approve
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleAction(signal.id, 'reject')}
                        disabled={isLoading}
                        className="px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        Reject
                      </motion.button>
                    </div>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : signal.id)}
                      className="text-xs text-gray-500 hover:text-primary-700 flex items-center gap-1 transition-colors"
                    >
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {isExpanded ? 'Hide details' : 'Sources & Contact'}
                    </button>
                  </div>
                </div>

                {/* Expanded full details */}
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="border-t border-gray-100 bg-gray-50 p-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Sources */}
                      <div>
                        <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                          Sources ({signal.source_count || (signal.source_urls || []).length || 0})
                        </h5>
                        {signal.source_urls && signal.source_urls.length > 0 ? (
                          <div className="space-y-1.5">
                            {signal.source_urls.map((url, i) => {
                              let displayUrl = url;
                              try { displayUrl = new URL(url).hostname + new URL(url).pathname.slice(0, 40); } catch {}
                              return (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-xs text-primary-600 hover:text-primary-800 hover:bg-primary-50 px-2 py-1 rounded transition-colors"
                                >
                                  <ExternalLink className="w-3 h-3 shrink-0" />
                                  <span className="truncate">{displayUrl}</span>
                                </a>
                              );
                            })}
                          </div>
                        ) : signal.scored_signal_sources && signal.scored_signal_sources.length > 0 ? (
                          <div className="space-y-1">
                            {signal.scored_signal_sources.map((src) => (
                              <div key={src.id} className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="bg-gray-200 px-1.5 py-0.5 rounded font-medium">{src.event_type}</span>
                                {src.contribution_note && <span>{src.contribution_note}</span>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No source data available</p>
                        )}
                      </div>

                      {/* Contact Information */}
                      <div>
                        <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Contact Information</h5>
                        {contact ? (
                          <div className="space-y-2 text-sm">
                            {contact.company_name && (
                              <div className="flex gap-2">
                                <span className="text-gray-500 text-xs w-16 shrink-0">Company:</span>
                                <span className="text-gray-800 text-xs font-medium">{contact.company_name}</span>
                              </div>
                            )}
                            {contact.website && (
                              <div className="flex gap-2">
                                <span className="text-gray-500 text-xs w-16 shrink-0">Website:</span>
                                <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">
                                  {contact.website}
                                </a>
                              </div>
                            )}
                            {contact.location && (
                              <div className="flex gap-2">
                                <span className="text-gray-500 text-xs w-16 shrink-0">Location:</span>
                                <span className="text-gray-800 text-xs">{contact.location}</span>
                              </div>
                            )}
                            {contact.key_person && (
                              <div className="flex gap-2">
                                <span className="text-gray-500 text-xs w-16 shrink-0">Contact:</span>
                                <span className="text-gray-800 text-xs">{contact.key_person}</span>
                              </div>
                            )}
                            {contact.email_domain && (
                              <div className="flex gap-2">
                                <span className="text-gray-500 text-xs w-16 shrink-0">Email:</span>
                                <span className="text-gray-800 text-xs">{contact.email_domain}</span>
                              </div>
                            )}
                            {contact.raw && !contact.company_name && (
                              <p className="text-xs text-gray-600">{contact.raw}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No contact data available</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-400">
                      Created: {new Date(signal.created_at).toLocaleString()}
                      {signal.updated_at !== signal.created_at && (
                        <span className="ml-4">Updated: {new Date(signal.updated_at).toLocaleString()}</span>
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Advanced pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <span className="text-sm text-gray-600">
            Showing {(page - 1) * pageSize + 1}{"\u2013"}{Math.min(page * pageSize, total)} of {total}
          </span>
          <nav className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPageNumbers().map(p => (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`w-9 h-9 text-sm font-medium rounded-lg transition-colors ${
                  p === page
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default SignalReviewQueue;
